/**
 * AnnexListPage — danh sách phụ lục hợp đồng. Đồng bộ ContractListView.
 */
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Snackbar, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import RoleGuard from '@/components/RoleGuard'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useDeleteAnnexMutation, useLazyPrintAnnexQuery, useListAnnexesQuery } from '@/store/api/annexesApi'
import type { AnnexOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import AnnexFormDialog from './AnnexFormDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type FilterKind = 'search'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = { key: Extract<keyof AnnexOut, string>; label: string; kind?: ColumnKind; align?: 'right'; filter?: ColumnFilterKind }
// Access không sum ID/price (price là đơn giá thay đổi, không phải metric cộng dồn).
const NON_SUM_TOTAL_KEYS = new Set<string>(['id', 'price'])

// 12 cột — map 1:1 theo ColumnOrder của Access form `frmPurchaseQryAnnex`.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'annex_no', label: 'Amendment No.' },
  { key: 'annex_date', label: 'Amendment Date.', kind: 'date' },
  { key: 'content_before_change', label: 'Content (Before)' },
  { key: 'content_after_change', label: 'Content (After)' },
  { key: 'price', label: 'Changed Price', kind: 'number', align: 'right' },
  { key: 'quantity', label: 'Changed Quantity', kind: 'number', align: 'right' },
  { key: 'contract_no', label: 'Contract No.', filter: 'checkboxes' },
  { key: 'supplier_name', label: 'Supplier', filter: 'checkboxes' },
  { key: 'note', label: 'Note' },
  { key: 'updated_date', label: 'Updated Date', kind: 'datetime' },
  { key: 'updated_by', label: 'Updated By' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessDateTime = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${formatAccessDate(v)} ${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}` }
const formatAccessNumber = (v: number): string => Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const truncate = (text: string | null | undefined, max = 80): string => !text ? '' : text.length > max ? `${text.slice(0, max)}…` : text

const cellValue = (row: AnnexOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (col.key === 'content_before_change' || col.key === 'content_after_change') return truncate(String(val))
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

export function AnnexListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const { selectedId, toggleSelect } = useRowSelection<number>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.annex')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof AnnexOut> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof AnnexOut
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => { setSearchTerm(''); columnFilters.clearAll(); setPage(0); closeFilterPopover() }

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<AnnexOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<AnnexOut | null>(null)
  const [printTarget, setPrintTarget] = useState<AnnexPrintTarget | null>(null)

  const { data, isLoading, isFetching, error } = useListAnnexesQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })
  const [deleteAnnex, { isLoading: deleting }] = useDeleteAnnexMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'annex',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.annex',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.length * 150),
    [visibleColumns.length],
  )

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
      if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
      return true
    }), [baseRows, searchTerm, columnFilters.filters])

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, visibleColumns), [filteredRows, sortState, visibleColumns])
  const rows = useMemo(() => rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(String(col.key))).map(
          (col) => [String(col.key), sumNumericColumn(filteredRows, col.key)],
        ),
      ),
    [filteredRows, visibleColumns],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.id === selectedId) ?? null, [baseRows, selectedId])
  const toggleSort = (key: keyof AnnexOut) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  const handleEdit = () => { if (selectedRow) setEditing(selectedRow) }
  const handlePrint = () => {
    if (!selectedRow) return
    // Defensive: chỉ trigger nếu `id` là số nguyên hợp lệ — tránh URL
    // `/purchase/annexes/{annex_id}/print` nhận chuỗi (BE Pydantic v2 sẽ
    // reject với "Input should be a valid integer …").
    const id = Number(selectedRow.id)
    if (!Number.isInteger(id) || id <= 0) {
      snack.error(`Invalid amendment id (id=${String(selectedRow.id)}). Please reload and reselect.`)
      return
    }
    setPrintTarget({ id, annexNo: selectedRow.annex_no ?? '' })
  }
  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try { await deleteAnnex(confirmDel.id).unwrap(); snack.success(`Deleted ${confirmDel.annex_no}`); setConfirmDel(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Failed to delete amendment')) }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
            <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleEdit}>Edit</Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => selectedRow && setConfirmDel(selectedRow)}>Delete</Button>
            <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={handlePrint}>Print</Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openMenu(e.currentTarget)} sx={filterBarBtnSx}>Columns</Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.68rem' }, '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' } }} />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Amendment no, contract no, supplier..."
              label="Search Amendments"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(String(colPopover.key), v); setPage(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Amendments"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Amendments found"
              description="Try adjusting the search, date range, or column filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const w = getWidth(String(col.key))
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(String(col.key))}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const total = totalRowValues.get(String(col.key))
                    const val = total == null ? '' : formatAccessNumber(total)
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: AnnexOut) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const w = getWidth(String(col.key))
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const id = Number.isFinite(idx) ? rows[idx]?.id : null
                  return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) toggleSelect(id) }} />
                },
              }}
            />
          )}
        </Box>
      </Box>

      <AnnexFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <AnnexFormDialog open={!!editing} mode="edit" initialValue={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog open={!!confirmDel} title="Delete Amendment" message={confirmDel ? `Delete amendment ${confirmDel.annex_no} of ${confirmDel.contract_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDel(null)} />
      <AnnexPrintDialog
        open={Boolean(printTarget)}
        printTarget={printTarget}
        onClose={() => setPrintTarget(null)}
      />
    </Box>
  )
}

/* ─── Print Preview (Access btnPrint_Click → rpPurchaseAmendment) ──────────
   Auto-fetch HTML preview khi mở, render qua iframe `srcDoc`. Nút Print
   upload HTML qua `printAndUploadReport` (BE re-render PDF cùng template). */

type AnnexPrintTarget = { id: number; annexNo: string }

interface AnnexPrintDialogProps {
  open: boolean
  printTarget: AnnexPrintTarget | null
  onClose: () => void
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

function buildAmendmentPreviewHtml(rawHtml: string): string {
  if (!rawHtml) return ''
  if (rawHtml.includes('<base ')) return rawHtml
  return rawHtml.replace('<head>', `<head><base href="${window.location.origin}/">`)
}

function AnnexPrintDialog({ open, printTarget, onClose }: AnnexPrintDialogProps) {
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewKey, setPreviewKey] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [triggerPrint, { isFetching }] = useLazyPrintAnnexQuery()
  const autoPreviewKeyRef = useRef('')

  const annexId = printTarget?.id ?? 0
  const annexNo = printTarget?.annexNo ?? ''
  const cacheKey = annexId ? String(annexId) : ''

  const handleClosePreview = useCallback(() => {
    setPreviewHtml('')
    setPreviewKey('')
  }, [])

  useEffect(() => {
    if (!open) {
      autoPreviewKeyRef.current = ''
      return
    }
    // Hard guard: không trigger khi id không phải số nguyên dương — tránh
    // URL `/purchase/annexes/undefined/print` (BE Pydantic v2 reject 422).
    if (!Number.isInteger(annexId) || annexId <= 0) return
    if (autoPreviewKeyRef.current === cacheKey) return
    autoPreviewKeyRef.current = cacheKey
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await triggerPrint(annexId).unwrap()
          if (!result.data.html) {
            setErrorMessage('Print preview could not open because the server did not return a document.')
            onClose()
            return
          }
          setPreviewHtml(buildAmendmentPreviewHtml(result.data.html))
          setPreviewKey(annexNo || cacheKey)
          setToastMessage(result.message ?? 'Print preview generated.')
          onClose()
        } catch (err) {
          setErrorMessage(getErrorMessage(err, 'Print Amendment failed'))
          onClose()
        }
      })()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [open, annexId, annexNo, cacheKey, triggerPrint, onClose])

  return (
    <>
      <Dialog open={open && isFetching && !previewHtml} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Generating print preview…</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(previewHtml)} onClose={handleClosePreview} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span">Print Preview - Amendment</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClosePreview} size="small">Close</Button>
            <Button
              variant="contained"
              size="small"
              disabled={!previewHtml}
              onClick={() => {
                const printTitle = `${sanitizePdfFileNamePart(previewKey || 'amendment')}_Amendment.pdf`
                void printAndUploadReport({
                  iframeTitle: 'Amendment Preview',
                  reportName: 'rpPurchaseAmendment',
                  printTitle,
                  html: previewHtml,
                  onUploadError: setToastMessage,
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title="Amendment Preview"
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(errorMessage)} onClose={() => setErrorMessage('')} maxWidth="xs" fullWidth>
        <DialogTitle>Print Error</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" size="small" onClick={() => setErrorMessage('')}>OK</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}

export default AnnexListPage
